import React, { FC, ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import { useAudioTrigger } from '@pp/core/src/controllers/audioController/hooks';
import { useMedia } from '@pp/core/src/hooks/useMedia';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { GameVolumeChannels } from '../../config/sound';
import { useGameConfigFetcher } from '../../store/apiHooks/useGameConfigFetcher';
import { airplaneStates } from '../../store/slices/highflyerAirPlane/types';

import { Airplane } from './airplane/Airplane';
import { AirplaneReady } from './airplane/AirplaneReady';
import { Parabola } from './airplane/Parabola';
import DarkBg from './DarkBg';
import { Jackpot } from './Jackpot/Jackpot';

import styles from './AirplaneAnimation.module.css';

export interface AirPlaneAnimationInterface {
  airplaneCurrentState: airplaneStates;
  multiplier: number;
  isSBBEnabled: boolean;
  isChatClosed: boolean;
  isJackpot: boolean;
  isAnimating: boolean;
  isAnimationToggle: boolean;
  theme: string;
  isFlagAvailable: boolean;
}

declare global {
  interface Window {
    flyingObjectFallback: string;
  }
}

export const Animation: FC<AirPlaneAnimationInterface> = ({
  airplaneCurrentState,
  multiplier,
  isJackpot,
  isSBBEnabled,
  isChatClosed,
  isAnimating,
  isAnimationToggle,
  theme,
  isFlagAvailable,
}): ReactElement => {
  const gameConfigDataFetcher = useGameConfigFetcher();
  useEffect(() => {
    gameConfigDataFetcher();
  }, [gameConfigDataFetcher]);
  const airplaneBoxRef = useRef<HTMLDivElement>(null);
  const [bgColorClass, setBgColorClass] = useState(`#1E242E`);
  const [airplaneWidthHeight, setAirplaneWidthHeight] = useState({
    airplaneBoxWidth: 0,
    airplaneBoxHeight: 0,
    planeWidth: 0,
    PlaneHeight: 0,
    flagWidth: 0,
    flagHeight: 0,
    propellerWidth: 0,
    propellerHeight: 0,
    scaleValue: 0,
  });
  const [orientation, setOrientation] = useState(true);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const defaultFlagURL = './assets/themes/default/animation/flag.svg';
  const defaultPlaneURL = './assets/themes/default/animation/plane.svg';

  const [flag, setFlag] = useState<string>('');
  const [plane, setPlane] = useState<string>('');

  const audioTrigger = useAudioTrigger(GameVolumeChannels.Background);
  const inDesktopMode = useMedia('(min-width: 1024px');
  const motionID = useRef(0);
  const gameType = useConfigSlice.getGameType();

  useEffect(() => {
    if (!gameType || !theme) return; // Prevent running with undefined values

    const BASE_URL = `${window.location.origin}/apps/themes/1.0.0/games/${gameType}`;
    const flagURL = `${BASE_URL}/${theme}/animation/flag.svg`;
    const planeURL = `${BASE_URL}/${theme}/animation/plane.svg`;

    const fetchImages = async () => {
      try {
        const flagResponse = await fetch(flagURL);
        if (flagResponse.ok) setFlag(flagURL);
      } catch (error) {
        console.error('Error fetching the flag:', error);
        setFlag(defaultFlagURL);
      }

      try {
        const planeResponse = await fetch(planeURL);
        if (planeResponse.ok) setPlane(planeURL);
      } catch (error) {
        console.error('Error fetching the Plane:', error);
        setPlane(defaultPlaneURL);
      }
    };

    fetchImages();
  }, [theme, gameType]); // ✅ Added gameType as dependency

  useEffect(() => {
    setBgColorClass(() => {
      if (airplaneCurrentState === airplaneStates.FLYING) {
        if (multiplier < 2) {
          return 'less2';
        } else if (multiplier >= 2 && multiplier < 6) {
          return 'less6';
        } else if (multiplier >= 6 && multiplier < 26) {
          return 'less26';
        } else if (multiplier >= 26 && multiplier < 101) {
          return 'less101';
        } else if (multiplier >= 101) {
          return 'less5000';
        }
      }
      return 'default';
    });
  }, [multiplier, airplaneCurrentState]);

  const updateWidthAndHeight = useCallback((): void => {
    if (!airplaneBoxRef.current) return;
    const width = airplaneBoxRef.current.clientWidth;
    const height = airplaneBoxRef.current.clientHeight;
    const scaleX = width / 900;
    setAirplaneWidthHeight({
      airplaneBoxWidth: width,
      airplaneBoxHeight: height,
      planeWidth: Math.round(186 * scaleX),
      PlaneHeight: Math.round(95 * scaleX),
      flagWidth: Math.round(1054 * scaleX),
      flagHeight: Math.round(290 * scaleX),
      propellerWidth: Math.round(71 * scaleX),
      propellerHeight: Math.round(135 * scaleX),
      scaleValue: scaleX,
    });
  }, []);

  useEffect(() => {
    if (airplaneCurrentState === airplaneStates.FLYING) {
      motionID.current = Math.floor(Math.random() * 1000000);
    }
  }, [airplaneCurrentState]);

  useEffect(() => {
    updateWidthAndHeight();
  }, [isSBBEnabled, isChatClosed, isPageVisible]);

  useEffect(() => {
    if (isJackpot) audioTrigger.play({ name: 'jackpot' });
  }, [isJackpot, audioTrigger]);

  useEffect(() => {
    updateWidthAndHeight();
    const handleResize = (): void => {
      updateWidthAndHeight();
      setTimeout(() => {
        updateWidthAndHeight();
      }, 500);
    };

    const handleOrientationChange = (): void => {
      setOrientation(false);
      setTimeout(() => {
        setOrientation(true);
        updateWidthAndHeight();
      }, 500);
    };
    const handleVisibilityChange = (): void => {
      setIsPageVisible(!document.hidden);
    };
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleResize);
    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const colorMods = useModifiers(styles, 'bg', {
    [bgColorClass]: airplaneCurrentState === airplaneStates.FLYING,
  });

  return (
    <>
      <div className={styles.airplaneBox} ref={airplaneBoxRef}>
        <div
          className={
            theme == 'default' ? `${styles.airplaneContainer}` : `${styles.airplaneContainer} ${styles.beSporkTheme}`
          }
        >
          <div className={`${styles.airplaneGB}`}></div>
          <DarkBg airplaneCurrentState={airplaneCurrentState} />
          <div
            className={`${styles.sunshine}
              ${airplaneCurrentState === airplaneStates.FLYING && isAnimationToggle ? `${styles.rotate}` : ''}`}
          ></div>
          {<div className={`${styles.multiplerBGColor} ${colorMods}`}></div>}
          {isJackpot && <Jackpot />}
          <div className={styles.svgBox}>
            {isAnimationToggle && orientation && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                preserveAspectRatio="none"
                className={`${styles.highFlyAnimation}`}
              >
                <Parabola
                  airplaneCurrentState={airplaneCurrentState}
                  motionID={motionID.current}
                  isAnimating={isAnimating}
                  airplaneWH={airplaneWidthHeight}
                  inDesktopMode={inDesktopMode}
                />
                <Airplane
                  airplaneCurrentState={airplaneCurrentState}
                  motionID={motionID.current}
                  isAnimating={isAnimating}
                  airplaneWH={airplaneWidthHeight}
                  theme={theme}
                  inDesktopMode={inDesktopMode}
                  isFlagAvailable={isFlagAvailable}
                  flagURL={flag}
                  planeURL={plane}
                />
                <AirplaneReady
                  isReadyToFly={airplaneCurrentState === airplaneStates.READY_TO_FLY}
                  airplaneWH={airplaneWidthHeight}
                  theme={theme}
                  planeURL={plane}
                />
              </svg>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
